import { FC } from 'react';
import { Form, Input, Button, Select, Tooltip, AutoComplete, FormInstance } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { IAddress, YESNO } from '../../../constants';
import usePlacesAutocomplete, { getGeocode, getLatLng, getZipCode } from 'use-places-autocomplete';
import { FormListFieldData } from 'antd/lib/form/FormList';
import { ScrollToView } from 'components/ScrollToView';
import { PopoverDelete } from 'components/popovers/popover-delete';
import { FormItemControl } from 'components/form/form-item-control/form-item-control';

const INTRAORAL_SCANNER_OPTIONS = [
  {
    value: 'iTero',
    label: 'iTero',
  },
  {
    value: 'Medit',
    label: 'Medit',
  },
  {
    value: 'Primescan',
    label: 'Primescan',
  },
  {
    value: 'Carestream',
    label: 'Carestream',
  },
  {
    value: 'Trios',
    label: 'Trios',
  },
  {
    value: 'Other',
    label: 'Other',
  },
];

interface IClinicItemProps {
  form: FormInstance<any>;
  formListName: string;
  index: number;
  field: FormListFieldData;
  onRemove: () => void;
  onFormSetFieldsValueAddress: (address: IAddress) => void;
}

export const ClinicItem: FC<IClinicItemProps> = ({
  form,
  formListName,
  index,
  field,
  onRemove,
  onFormSetFieldsValueAddress,
}) => {
  const { key, name, ...restField } = field;

  const {
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {},
    debounce: 300,
  });

  const handleAddressChange = (val: any) => {
    setValue(val);
  };

  const handleAddressSelect = ({ description, terms }: any) => {
    // When user selects a place, we can replace the keyword without request data from API
    // by setting the second parameter to "false"
    // setValue(description, false);
    clearSuggestions();

    // Get latitude and longitude via utility functions
    getGeocode({ address: description })
      .then(async (results) => {
        const latLng = await getLatLng(results[0]);
        const zipCode = await getZipCode(results[0], false);
        return { ...latLng, zipCode };
      })
      .then(({ lat, lng, zipCode }) => {
        const address: IAddress = {};
        const [country, state] = [...terms].reverse(); // the array length is dynamic but the last item is always country
        address.formattedAddress = description;
        address.latitude = lat;
        address.longitude = lng;
        address.zipCode = zipCode ?? '';
        address.postCode = zipCode ?? '';
        address.country = country?.value ?? '';
        address.state = state?.value ?? '';

        onFormSetFieldsValueAddress(address);
      })
      .catch((_error) => {
        // TODO: add error handling here
      });
  };

  return (
    <>
      <ScrollToView smooth block={index === 0 ? 'center' : 'start'} />
      <div className={`grid ${index > 0 ? 'mt-3 border-top mb-2' : ''}`}>
        {index > 0 && (
          <div className="col-12 mt-2">
            <div className="item-remove-container">
              <div className="practises-text-container">
                <span>Clinic {index + 1}</span>
              </div>
              <PopoverDelete onDelete={onRemove} placement="leftTop">
                <Tooltip title="Delete">
                  <Button shape="circle" icon={<CloseOutlined />} />
                </Tooltip>
              </PopoverDelete>
            </div>
          </div>
        )}
        <div className="col-12 md:col-6">
          <FormItemControl
            form={form}
            formListName={formListName}
            {...restField}
            label="Clinic Name*"
            name={[name, 'clinicName']}
            rules={[{ required: true, message: 'Please tell us where you work' }]}
          >
            <Input className="custom-input" placeholder="Enter clinic name" />
          </FormItemControl>
        </div>

        <div className="col-12 md:col-6">
          <FormItemControl
            form={form}
            formListName={formListName}
            {...restField}
            label="Clinic Address *"
            name={[name, 'address', 'formattedAddress']}
            rules={[{ required: true, message: 'Please add the clinic address so we know where to send things' }]}
          >
            <AutoComplete
              className="custom-input"
              value={value}
              onChange={handleAddressChange}
              placeholder="Enter clinic address"
            >
              {status === 'OK' &&
                data.map((suggestion) => {
                  const {
                    place_id,
                    structured_formatting: { main_text, secondary_text },
                  } = suggestion;
                  return (
                    <AutoComplete.Option
                      className="places-options"
                      key={place_id}
                      onClick={() => handleAddressSelect(suggestion)}
                      value={`${main_text} ${secondary_text || ''}`}
                    >
                      <div key={secondary_text} onClick={() => handleAddressSelect(suggestion)}>
                        <strong>{main_text}</strong> <small>{secondary_text}</small>
                      </div>
                    </AutoComplete.Option>
                  );
                })}
            </AutoComplete>
          </FormItemControl>
        </div>

        <div className="col-12 md:col-6">
          <FormItemControl
            form={form}
            formListName={formListName}
            {...restField}
            label="Clinic Email"
            name={[name, 'manager', 'email']}
            rules={[{ type: 'email', message: 'Please enter your full email address' }]}
            normalize={(fieldValue) => fieldValue?.toLowerCase()}
          >
            <Input className="custom-input" placeholder="Enter clinic email" />
          </FormItemControl>
          <div style={{ fontSize: '10px', color: '#808080' }}>
            Don’t worry if you don’t have this yet, we’ll check when you onboard with us
          </div>
        </div>

        <div className="col-12 md:col-6">
          <FormItemControl
            form={form}
            formListName={formListName}
            {...restField}
            label="Clinic Postcode *"
            name={[name, 'address', 'postCode']}
            rules={[{ required: true, message: 'Please enter your clinic post code or city' }]}
          >
            <Input className="custom-input" placeholder="Enter post code or city" />
          </FormItemControl>
        </div>

        <div className="col-12 md:col-6">
          <FormItemControl
            form={form}
            formListName={formListName}
            {...restField}
            label="Do you have a scanner in the practice? *"
            name={[name, 'scannerAvailable']}
            rules={[{ required: true, message: 'Please select option' }]}
          >
            <Select className="custom-input" options={YESNO} placeholder="Select Option" />
          </FormItemControl>
        </div>

        <div className="col-12 md:col-6">
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.clinics?.[index]?.scannerAvailable !== currentValues.clinics?.[index]?.scannerAvailable
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('clinics')?.[index]?.scannerAvailable === 'Yes' ? (
                <FormItemControl
                  form={form}
                  formListName={formListName}
                  {...restField}
                  label="Scanner type"
                  name={[name, 'scannerType']}
                >
                  <Select
                    className="custom-input"
                    options={INTRAORAL_SCANNER_OPTIONS}
                    mode="multiple"
                    placeholder="Select Option"
                    showArrow
                  />
                </FormItemControl>
              ) : null
            }
          </Form.Item>
        </div>
      </div>
    </>
  );
};
